// Copyright (c) 2026 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Rafael Pereira Satyro

import React from 'react';
import { AppIcon } from '@loa/ui-components';
import type { ProfileRequestStatus, RequestActionDTO } from '../../types/profileRequest';
import type { RequestOverviewDisplayStatus } from '../../types/requestOverview';
import './RequestDisplayStatusBadge.css';

/**
 * Human-readable copy for {@link RequestOverviewDisplayStatus}. Kept in sync
 * with the backend `RequestOverviewDisplayStatus` enum.
 */
const DISPLAY_STATUS_LABELS: Record<RequestOverviewDisplayStatus, string> = {
  COMPLETE: 'Complete',
  PENDING_PROVIDER: 'Pending Provider',
  PENDING_CLIENT: 'Pending Client',
  PENDING_ADVISOR: 'Pending Review',
  REJECTED: 'Rejected',
  EXPIRED: 'Expired',
  VOIDED: 'Voided',
  FAILED: 'Failed',
};

/** Human-readable label for a display status (e.g. for filter dropdowns). */
export function displayStatusLabel(displayStatus: RequestOverviewDisplayStatus): string {
  return DISPLAY_STATUS_LABELS[displayStatus] ?? displayStatus;
}

function statusModifier(displayStatus: RequestOverviewDisplayStatus): string {
  switch (displayStatus) {
    case 'COMPLETE': return 'complete';
    case 'PENDING_PROVIDER':
    case 'PENDING_CLIENT':
    case 'PENDING_ADVISOR': return 'pending';
    case 'REJECTED': return 'rejected';
    case 'EXPIRED': return 'expired';
    case 'VOIDED': return 'voided';
    case 'FAILED': return 'failed';
  }
}

function statusIcon(displayStatus: RequestOverviewDisplayStatus): string {
  switch (displayStatus) {
    case 'COMPLETE': return 'checkbox';
    case 'PENDING_PROVIDER':
    case 'PENDING_CLIENT':
    case 'PENDING_ADVISOR': return 'clock';
    case 'REJECTED':
    case 'FAILED': return 'rejected';
    case 'EXPIRED': return 'invite not sent';
    case 'VOIDED': return 'archive box';
  }
}

/**
 * Derive the UI-facing {@link RequestOverviewDisplayStatus} from a request's
 * raw status + ordered actions. Mirrors the resource-server
 * `ProfileRequestOverviewService.deriveDisplayStatus`: terminal statuses map
 * 1:1; non-terminal statuses split by the actor that owns the next pending
 * action (lowest sequenceOrder), defaulting to PROVIDER when nothing is pending.
 */
export function deriveDisplayStatus(
  status: ProfileRequestStatus,
  actions: RequestActionDTO[],
): RequestOverviewDisplayStatus {
  switch (status) {
    case 'COMPLETED': return 'COMPLETE';
    case 'REJECTED': return 'REJECTED';
    case 'EXPIRED': return 'EXPIRED';
    case 'VOIDED': return 'VOIDED';
    case 'FAILED': return 'FAILED';
    default: {
      const next = actions
        .filter((a) => a.status === 'PENDING' || a.status === 'IN_PROGRESS')
        .sort((a, b) => a.sequenceOrder - b.sequenceOrder)[0];
      if (!next) return 'PENDING_PROVIDER';
      if (next.assignedActorType === 'PROVIDER') return 'PENDING_PROVIDER';
      // The advisor's own step (AML document review) — mirrors the backend
      // actor-aware mapping.
      if (next.assignedActorType === 'ADVISOR' || next.assignedActorType === 'SUPPORT_STAFF') {
        return 'PENDING_ADVISOR';
      }
      return 'PENDING_CLIENT';
    }
  }
}

/**
 * Status pill with icon — the canonical request-status badge shared by the
 * dashboard Request Overview card and the relationship Requests tab.
 */
export const RequestDisplayStatusBadge: React.FC<{ status: RequestOverviewDisplayStatus }> = ({
  status,
}) => (
  <span
    className={`request-status-pill request-status-pill--${statusModifier(status)}`}
    data-testid={`status-pill-${status}`}
  >
    <AppIcon name={statusIcon(status)} size={14} />
    {DISPLAY_STATUS_LABELS[status] ?? status}
  </span>
);
